import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { Storage as ShellStorage } from "@groot/shell/pages/storage/Storage";
import type { StorageFile } from "@groot/shell/pages/storage/hooks/useStorage";
import { FileViewer } from "./components/FileViewer";

/**
 * Finance Storage page — wraps the groot shell Storage with the app-only
 * in-app file viewer (PDF/image/JSON/CSV/text preview) and the `?path=<file>`
 * deep-link auto-open behavior.
 */
export default function Storage() {
  const [currentPath, setCurrentPath] = useQueryState("path", parseAsString.withDefault(""));
  const [viewerFile, setViewerFile] = useState<{ filePath: string; fileName: string } | null>(null);

  // Deep-link: if ?path=<file> points at a file (has an extension), open the
  // viewer and rewrite path to the parent folder so the file list still renders.
  useEffect(() => {
    if (currentPath && !currentPath.endsWith("/")) {
      const fileName = currentPath.split("/").pop() || "";
      const hasExtension = fileName.includes(".") && !fileName.startsWith(".");
      if (hasExtension) {
        setViewerFile({ filePath: currentPath, fileName });
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf("/") + 1);
        setCurrentPath(parentPath);
      }
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: initial-load only
  }, []);

  const handleView = (file: StorageFile) => {
    if (file.isDirectory) return;
    setViewerFile({ filePath: file.key, fileName: file.name });
  };

  return (
    <>
      <ShellStorage onView={handleView} />
      {viewerFile && (
        <FileViewer
          filePath={viewerFile.filePath}
          fileName={viewerFile.fileName}
          onClose={() => setViewerFile(null)}
        />
      )}
    </>
  );
}
